import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import styles from './CollectionDetail.module.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function CollectionDetail() {
  const { slug } = useParams();
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCollection = async () => {
      try {
        const res = await fetch(`${API_URL}/collections/${slug}`);
        if (!res.ok) throw new Error(`HTTP ${res.status} - Collection not found`);
        const data = await res.json();
        setCollection(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCollection();
  }, [slug]);

  if (loading) {
    return (
      <main className={styles.main}>
        <div className={styles.container}>
          <div className={styles.loading}>
            <Icon icon="lucide:loader-2" className={styles.spinner} />
            Loading collection...
          </div>
        </div>
      </main>
    );
  }

  if (error || !collection) {
    return (
      <main className={styles.main}>
        <div className={styles.container}>
          <div className={styles.error}>
            <Icon icon="lucide:alert-triangle" />
            {error || 'Collection not found'}
          </div>
          <Link to="/collections" className={styles.backLink}>
            ← Back to Collections
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.main}>
      <div className={styles.container}>
        {/* Breadcrumb / Back */}
        <nav className={styles.breadcrumb}>
          <Link to="/" className={styles.breadLink}>Home</Link>
          <span>→</span>
          <Link to="/collections" className={styles.breadLink}>Collections</Link>
          <span>→</span>
          <span className={styles.current}>{collection.title}</span>
        </nav>

        {/* Collection Header */}
        <header className={styles.header}>
          <h1 className={styles.title}>{collection.title}</h1>
          <p className={styles.description}>{collection.description}</p>

          <div className={styles.meta}>
            <span className={styles.metaItem}>
              <Icon icon="lucide:package" />
              {collection.resources?.length || 0} resources
            </span>
            <span className={styles.metaItem}>
              <Icon icon="lucide:star" />
              {collection.rating || "No ratings yet"}
            </span>
            <span className={styles.metaItem}>
              <Icon icon="lucide:calendar" />
              Updated {new Date(collection.updatedAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
          </div>

          <div className={styles.actions}>
            <button className={styles.btnPrimary}>
              <Icon icon="lucide:bookmark-plus" />
              Save Collection
            </button>
            <button className={styles.btnOutline}>
              <Icon icon="lucide:share-2" />
              Share
            </button>
          </div>

          <div className={styles.tags}>
            {collection.tags?.map((tag, i) => (
              <span key={i} className={styles.tag}>{tag}</span>
            ))}
          </div>
        </header>

        {/* Resources in this Collection */}
        <section className={styles.resourcesSection}>
          <h2 className={styles.sectionTitle}>Resources in this Collection</h2>

          {collection.resources?.length === 0 ? (
            <div className={styles.empty}>
              No resources found in this collection yet.
            </div>
          ) : (
            <div className={styles.grid}>
              {collection.resources.map((res) => (
                <Link to={`/resource/${res.id}`} key={res.id} className={styles.resourceCard}>
                  <div className={styles.cardPlaceholder}></div>
                  <div className={styles.cardContent}>
                    <span className={styles.cardType}>{res.type}</span>
                    <h3 className={styles.cardTitle}>{res.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Actions at bottom */}
        <div className={styles.bottomActions}>
          <Link to="/collections" className={styles.backBtn}>
            ← Back to All Collections
          </Link>
        </div>
      </div>
    </main>
  );
}
// import { Link, useParams } from 'react-router-dom';
// import { Icon } from '@iconify/react';
// import styles from './CollectionDetail.module.css';

// // Dummy collection data (in real app: fetch by slug from useParams)
// const dummyCollections = {
//   'essential-toolkit': {
//     title: "Essential Safeguarding Toolkit 2026",
//     description: "Comprehensive bundle containing digital security basics, emergency contacts, legal templates, mental health resources — updated for current threats.",
//     count: 248,
//     rating: "4.9 (1,204 ratings)",
//     updated: "March 2026",
//     tags: ["Comprehensive", "Multilingual", "High Priority"],
//     resources: [
//       { id: 1, title: "Digital Security Checklist (2026)", type: "Guide", link: "/resource/digital-checklist-2026" },
//       { id: 2, title: "Organizational Security Policy Template", type: "Template", link: "/resource/policy-template" },
//       { id: 3, title: "Emergency Digital Security Helpdesk", type: "Contact", link: "/support" },
//       { id: 4, title: "Secure Communication Tools Comparison", type: "Guide", link: "/resource/comm-tools" },
//       { id: 5, title: "Mental Health Crisis Response Guide", type: "Guide", link: "/resource/mental-crisis" }
//     ]
//   },
//   'digital-fundamentals': {
//     title: "Digital Security Fundamentals",
//     description: "Core materials for device protection, secure communication, threat modeling, and best practices for high-risk users.",
//     count: 42,
//     rating: "4.7 (318 ratings)",
//     updated: "February 2026",
//     tags: ["Digital", "Beginner-Friendly", "High-Risk"],
//     resources: [
//       { id: 6, title: "Mobile Device Hardening Checklist", type: "Checklist", link: "/resource/mobile-hardening" },
//       { id: 7, title: "Encrypted Messaging Setup Guide", type: "Guide", link: "/resource/messaging-guide" },
//       { id: 8, title: "Threat Modeling Workbook", type: "Template", link: "/resource/threat-model" }
//     ]
//   }
//   // Add more slugs as needed
// };

// export default function CollectionDetail() {
//   const { slug } = useParams();
//   const collection = dummyCollections[slug] || {
//     title: "Collection Not Found",
//     description: "The requested collection could not be found.",
//     count: 0,
//     resources: []
//   };

//   return (
//     <main className={styles.main}>
//       <div className={styles.container}>
//         {/* Breadcrumb / Back */}
//         <nav className={styles.breadcrumb}>
//           <Link to="/" className={styles.breadLink}>Home</Link>
//           <span>→</span>
//           <Link to="/collections" className={styles.breadLink}>Collections</Link>
//           <span>→</span>
//           <span className={styles.current}>{collection.title}</span>
//         </nav>

//         {/* Collection Header */}
//         <header className={styles.header}>
//           <h1 className={styles.title}>{collection.title}</h1>
//           <p className={styles.description}>{collection.description}</p>

//           <div className={styles.meta}>
//             <span className={styles.metaItem}>
//               <Icon icon="lucide:package" />
//               {collection.count} resources
//             </span>
//             <span className={styles.metaItem}>
//               <Icon icon="lucide:star" />
//               {collection.rating || "No ratings yet"}
//             </span>
//             <span className={styles.metaItem}>
//               <Icon icon="lucide:calendar" />
//               Updated {collection.updated}
//             </span>
//           </div>

//           <div className={styles.actions}>
//             <button className={styles.btnPrimary}>
//               <Icon icon="lucide:bookmark-plus" />
//               Save Collection
//             </button>
//             <button className={styles.btnOutline}>
//               <Icon icon="lucide:share-2" />
//               Share
//             </button>
//           </div>

//           <div className={styles.tags}>
//             {collection.tags?.map((tag, i) => (
//               <span key={i} className={styles.tag}>{tag}</span>
//             ))}
//           </div>
//         </header>

//         {/* Resources in this Collection */}
//         <section className={styles.resourcesSection}>
//           <h2 className={styles.sectionTitle}>Resources in this Collection</h2>

//           {collection.resources.length === 0 ? (
//             <div className={styles.empty}>
//               No resources found in this collection yet.
//             </div>
//           ) : (
//             <div className={styles.grid}>
//               {collection.resources.map((res) => (
//                 <Link to={res.link} key={res.id} className={styles.resourceCard}>
//                   <div className={styles.cardPlaceholder}></div>
//                   <div className={styles.cardContent}>
//                     <span className={styles.cardType}>{res.type}</span>
//                     <h3 className={styles.cardTitle}>{res.title}</h3>
//                   </div>
//                 </Link>
//               ))}
//             </div>
//           )}
//         </section>

//         {/* Actions at bottom */}
//         <div className={styles.bottomActions}>
//           <Link to="/collections" className={styles.backBtn}>
//             ← Back to All Collections
//           </Link>
//         </div>
//       </div>
//     </main>
//   );
// }